/**
 * User Management › Partners (ops mock version 13, §B4; Jeff, 28 October 2026) — partner organisations and the
 * applications waiting on staff, across every zone.
 *
 * READS S4's partner briefs through its own API (`GET /admin/partner-applications/buckets`, read-only) and writes
 * nothing of S4's. Under ADR-116 D-A4 a partner is its brief and its account: partner standing is a property of the
 * account, and its people are that account's members — so the brief list IS the partner list.
 *
 * ⭐ THE CONSOLE'S EXISTING APPROVAL ACTIONS WIN (the brief: "the console's existing components win where they
 * exist"). Approve, reject, request info, deactivate and reactivate live on the brief's own page (StaffActionZone),
 * beside the assessment a decision needs. Each row links there rather than re-implementing a decision without its
 * evidence; the strip at the top links to the Applications queue and its partner invitation.
 *
 * The eye opens the contact's access panel. A brief names its contact by email, not by sign-in, so the eye looks the
 * person up first (every zone) and says so plainly when no sign-in exists for that address.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilterPills } from '../../../components/FilterPills';
import { FilterSearch } from '../../../components/FilterSearch';
import { ColumnTooltip } from '../../../components/ColumnTooltip';
import { ZoneBoardLegend } from '../../../components/ZoneBoardLegend';
import { ZoneCell, ZoneFanoutWarning } from '../../../components/ZoneIndicators';
import { useZoneReadReport } from '../../../components/ZoneContext';
import { useToast, toastFromError } from '../../../components/ToastProvider';
import { ZONES, type PortalZone } from '../../../lib/zones';
import { ALL_ZONES, countInZone, matchesZoneFilter, zoneBoardLegend } from '../../../lib/zone-board';
import { listBriefBuckets, type BriefListItem, type PartnerBriefStatus } from '../../../lib/partner-briefs-api';
import { listAllSubscribers } from '../../../lib/subscribers-api';
import { ActionIcon, LoadFailed, Loading, PersonEye, fmtDate, useZoneList, type OpenPerson } from './shared';

export type PartnerStage = 'applied' | 'invited' | 'active' | 'suspended';

/** Which stage of the programme each brief status is — the four pills. */
export const STAGE_OF: Partial<Record<PartnerBriefStatus, PartnerStage>> = {
  staff_review: 'applied',
  human_review_required: 'applied',
  pending_response: 'applied',
  info_requested: 'applied',
  manual_add_invited: 'invited',
  manual_add_expired: 'invited',
  approved: 'active',
  pending_backlink_verification: 'active',
  active: 'active',
  suspended: 'suspended',
};
export const PARTNER_STATUSES = Object.keys(STAGE_OF) as PartnerBriefStatus[];

const STAGE_BADGE: Record<PartnerStage, [string, string]> = {
  applied: ['portal-badge-pending', 'Applied'],
  invited: ['portal-badge-pending', 'Invited'],
  active: ['portal-badge-active', 'Active'],
  suspended: ['portal-badge-paused', 'Suspended'],
};
const ALL_STAGES: PartnerStage[] = ['applied', 'invited', 'active', 'suspended'];

export type PartnerRow = BriefListItem & { stage: PartnerStage };

const readPartners = async (z: PortalZone): Promise<PartnerRow[]> => {
  const { buckets } = await listBriefBuckets(PARTNER_STATUSES, z);
  return PARTNER_STATUSES.flatMap((s) => (buckets[s]?.items ?? []).map((b) => ({ ...b, stage: STAGE_OF[s]! })));
};

export function PartnersTab({ onOpenPerson }: { onOpenPerson: OpenPerson }) {
  const toast = useToast();
  const { rows, results, failed, failedZones, reload } = useZoneList<PartnerRow>(readPartners, 'the partners');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<PartnerStage>>(new Set(ALL_STAGES));
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [looking, setLooking] = useState<string | null>(null);

  const all = useMemo(() => rows ?? [], [rows]);
  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((p) => selected.has(p.stage) && matchesZoneFilter(p, zoneFilter)
        && (!q || `${p.businessName} ${p.applicantEmail} ${p.websiteUrl}`.toLowerCase().includes(q)))
      .sort((a, b) => ALL_STAGES.indexOf(a.stage) - ALL_STAGES.indexOf(b.stage) || a.businessName.localeCompare(b.businessName));
  }, [all, selected, zoneFilter, search]);
  const count = (k: PartnerStage) => all.filter((p) => p.stage === k).length;

  /** The brief names its contact by email — find their sign-in (every zone) before opening the panel. */
  const openContact = async (p: PartnerRow) => {
    const email = p.applicantEmail.trim().toLowerCase();
    setLooking(p.applicationId);
    try {
      const { subscribers } = await listAllSubscribers(email);
      const hit = subscribers.find((s) => s.email.toLowerCase() === email);
      if (hit?.zone) onOpenPerson(hit.cognitoSub, hit.zone);
      else toast.push({ variant: 'info', title: 'No sign-in for this contact', detail: `${p.applicantEmail} has no Nexanomics sign-in yet, so there is no access to show.` });
    } catch (err) {
      const { title, detail } = toastFromError(err, 'Could not look up the contact');
      toast.push({ variant: 'error', title, detail });
    } finally {
      setLooking(null);
    }
  };

  return (
    <section className="users-tab-panel" aria-label="Partners" data-testid="users-tab-partners">
      <h2 className="users-tab-heading">
        Partners
        {rows && <span className="portal-badge">{all.length} · {count('active')} active · {count('applied')} applied · {count('invited')} invited · {count('suspended')} suspended</span>}
      </h2>
      <p className="apps-strip" data-testid="partners-apps-strip">
        <span><b>{rows ? count('applied') : '—'} application{count('applied') === 1 ? '' : 's'} awaiting review</b> — approvals, and the partner invitation, live in the Applications queue.</span>
        <Link to="/admin/partners">Open Partners › Applications ›</Link>
      </p>
      <p className="panel-help">
        Partner organisations and the person who applied for each. A partner&rsquo;s people are the members of its own
        account. The eye opens the contact&rsquo;s access panel; the pencil opens the application, where it is approved,
        rejected, deactivated or reactivated.
      </p>

      <div className="portal-applications-controls">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search partner, contact or website…" ariaLabel="Search partners" />
        <FilterPills
          ariaLabel="Filter partners by stage"
          selected={selected}
          onChange={setSelected}
          groups={[
            { key: 'active', label: 'Active', icon: '✅', count: count('active') },
            { key: 'applied', label: 'Applied', icon: '📝', count: count('applied') },
            { key: 'invited', label: 'Invited', icon: '✉️', count: count('invited') },
            { key: 'suspended', label: 'Suspended', icon: '⏸️', count: count('suspended') },
          ]}
        />
      </div>
      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Zone
          <select className="portal-select" aria-label="Zone filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value={ALL_ZONES}>All zones</option>
            {ZONES.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
          </select>
        </label>
      </div>
      <ZoneFanoutWarning failedZones={failedZones} noun="partners" onRetry={() => void reload()} />
      <ZoneBoardLegend
        testId="users-partners-legend"
        ariaLabel="Partners by zone"
        loading={rows === null}
        columns={[{ key: 'active', label: 'Active' }, { key: 'applied', label: 'Applied' }, { key: 'invited', label: 'Invited' }, { key: 'suspended', label: 'Suspended' }]}
        rows={zoneBoardLegend(results, {
          counts: (code) => Object.fromEntries(ALL_STAGES.map((s) => [s, countInZone(all, code, (p) => p.stage === s)])),
        })}
      />
      <p className="legend-cap">Counted over every row, whatever the filter shows.</p>

      {rows === null && <Loading text="Loading the partners…" />}
      {failed && <LoadFailed what="the partners" onRetry={() => void reload()} />}
      {rows !== null && !failed && (
        <table className="portal-users-table" data-testid="users-partners-table">
          <thead>
            <tr>
              <th scope="col">Partner</th>
              <th scope="col">Contact</th>
              <th scope="col">Zone</th>
              <th scope="col">
                <span className="portal-col-help-wrap">Stage
                  <ColumnTooltip columnName="Stage" items={[
                    { term: 'Active', desc: 'An approved partner — every member of its account has the Partner dashboard.' },
                    { term: 'Applied', desc: 'Waiting on staff: review, a reply, or more information.' },
                    { term: 'Invited', desc: 'Invited by staff; the invitation is waiting to be taken up.' },
                    { term: 'Suspended', desc: 'Deactivated; reactivated from the application.' },
                  ]} />
                </span>
              </th>
              <th scope="col">Since</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={6} className="portal-users-empty">
                {selected.size === 0 ? 'No filter pills selected — click a stage pill to show partners.' : 'No partners match the current filters.'}
              </td></tr>
            )}
            {shown.map((p) => {
              const [cls, word] = STAGE_BADGE[p.stage];
              return (
                <tr key={`${p.zone}:${p.applicationId}`} data-stage={p.stage}>
                  <td>
                    <div className="portal-users-name">{p.businessName || 'Unnamed partner'}</div>
                    {p.websiteUrl && <div className="portal-users-sub-email">{p.websiteUrl}</div>}
                  </td>
                  <td>{p.applicantEmail || '—'}</td>
                  <td><ZoneCell zone={p.zone} /></td>
                  <td><span className={`portal-badge ${cls}`}>{word}</span></td>
                  <td className="portal-users-created">{fmtDate(p.reviewedAt ?? p.submittedAt)}</td>
                  <td className="portal-users-row-actions portal-actions-col">
                    <div className="portal-actions-row">
                      <PersonEye
                        name={p.applicantEmail || p.businessName}
                        onOpen={p.applicantEmail && looking !== p.applicationId ? () => void openContact(p) : undefined}
                        disabledTip={!p.applicantEmail ? 'No contact on this application' : undefined}
                      />
                      <ActionIcon
                        act="review"
                        icon="review"
                        label={`Open ${p.businessName || 'this partner'}'s application`}
                        tip={p.stage === 'applied'
                          ? 'Review the application — approve or reject there'
                          : 'Open the application — deactivate and reactivate happen there'}
                        href={`/admin/partners/${encodeURIComponent(p.applicationId)}${p.zone ? `?zone=${encodeURIComponent(p.zone)}` : ''}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
